/**
 * Clockwise API Service
 * All timesheet-related API calls for axo-people-front.
 * Mirrors the pattern used across the project's /services/ directory.
 */
import { API_URL } from '../config';
import projectsRaw from '../pages/clockwise/projects_data.json';

const CLOCKWISE_BASE = `${API_URL}/clockwise-tasks`;

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const handleResponse = async (res) => {
  const text = await res.text();

  if (!res.ok) {
    throw new Error(text || `HTTP ${res.status}`);
  }

  if (!text || res.status === 204) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

// ─── Master Data ──────────────────────────────────────────────────────────────
export const getClockwiseProjects = () =>
  fetch(`${API_URL}/clockwise/projects`, { headers: authHeaders() }).then(handleResponse);

export const getClockwiseEmployees = () =>
  fetch(`${API_URL}/clockwise/users`, { headers: authHeaders() }).then(handleResponse);

// ─── Local Projects Data (from bundled projects_data.json) ────────────────────
// Returns { projects, clients, services } derived from the local JSON file.
// Structure: projectsRaw is an array of project objects, each with a `clients`
// array, and each client has a `Services` (or `services`) array.
let _cachedProjectsData = null;

export const getLocalProjectsData = () => {
  if (_cachedProjectsData) return Promise.resolve(_cachedProjectsData);

  const projects = [];
  const clientsMap = new Map();
  const servicesMap = new Map();

  const addServices = (svcs) => {
    (svcs || []).forEach((svc) => {
      if (!servicesMap.has(svc.id)) {
        servicesMap.set(svc.id, {
          id: svc.id,
          service: svc.service,
          name: svc.service,
          department_id: svc.department_id,
          type: 'service',
        });
      }
    });
  };

  (projectsRaw || []).forEach((root) => {
    if (root?.id == null || !root?.name) return;

    // Treat every top-level tree as a selectable parent node.
    projects.push({
      id: root.id,
      name: root.name,
      type: 'project',
    });

    if (Array.isArray(root.clients)) {
      root.clients.forEach((client) => {
        if (!clientsMap.has(client.id)) {
          const svcs = client.Services || client.services || [];
          clientsMap.set(client.id, {
            id: client.id,
            name: client.name,
            project_id: root.id,
            type: 'client',
            Services: svcs,
          });
        }

        addServices(clientsMap.get(client.id)?.Services || []);
      });
    }

    if (Array.isArray(root.departments)) {
      root.departments.forEach((department) => {
        if (!clientsMap.has(department.id)) {
          const svcs = department.Services || department.services || [];
          clientsMap.set(department.id, {
            id: department.id,
            name: department.name,
            project_id: root.id,
            type: department.type || 'client',
            Services: svcs,
          });
        }

        addServices(clientsMap.get(department.id)?.Services || []);
      });
    }
  });

  _cachedProjectsData = {
    projects,
    clients: Array.from(clientsMap.values()),
    services: Array.from(servicesMap.values()),
    departments: [],
    externals: [],
  };

  return Promise.resolve(_cachedProjectsData);
};

// Keep legacy alias so any existing imports of getMarabesData still work
export const getMarabesData = getLocalProjectsData;

// ─── Week Data ────────────────────────────────────────────────────────────────
export const getClockwiseTasks = (year, week, userId = null) => {
  const params = new URLSearchParams({ year, week });
  if (userId) params.append('user_id', userId);
  return fetch(`${CLOCKWISE_BASE}?${params}`, { headers: authHeaders() }).then(handleResponse);
};

export const getClockwiseTasksForAllEmployees = (year, week) => {
  const params = new URLSearchParams({ year, week, all: 'true' });
  return fetch(`${CLOCKWISE_BASE}?${params}`, { headers: authHeaders() }).then(handleResponse);
};

export const getWeekStatus = (year, week, userId = null) => {
  const params = new URLSearchParams({ year, week });
  if (userId) params.append('user_id', userId);
  return fetch(`${CLOCKWISE_BASE}/status/week?${params}`, { headers: authHeaders() }).then(handleResponse);
};

export const getDailyStatus = (year, week, userId = null) => {
  const params = new URLSearchParams({ year, week });
  if (userId) params.append('user_id', userId);
  return fetch(`${CLOCKWISE_BASE}/status/daily?${params}`, { headers: authHeaders() }).then(handleResponse);
};

// ─── Task CRUD ────────────────────────────────────────────────────────────────
export const createTasks = (tasksOrTask) => {
  const isBulk = Array.isArray(tasksOrTask);
  const endpoint = isBulk ? `${CLOCKWISE_BASE}/bulk` : CLOCKWISE_BASE;
  return fetch(endpoint, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(tasksOrTask),
  }).then(handleResponse);
};

export const updateTask = (taskId, taskData) =>
  fetch(`${CLOCKWISE_BASE}/${taskId}`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(taskData),
  }).then(handleResponse);

export const deleteTask = (taskId) =>
  fetch(`${CLOCKWISE_BASE}/${taskId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  }).then(handleResponse);

// ─── Workflow Actions ─────────────────────────────────────────────────────────
export const handInWeek = (year, week, userId = null) =>
  fetch(`${CLOCKWISE_BASE}/hand-in`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ year, week, ...(userId ? { user_id: userId } : {}) }),
  }).then(handleResponse);

export const handInWeekAllUsers = (year, week) =>
  fetch(`${CLOCKWISE_BASE}/hand-in-all-users`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ year, week }),
  }).then(handleResponse);

export const reopenWeek = (year, week, userId) =>
  fetch(`${CLOCKWISE_BASE}/reopen`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ year, week, user_id: userId }),
  }).then(handleResponse);

export const approveWeek = (year, week, userId) =>
  fetch(`${CLOCKWISE_BASE}/approve`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ year, week, user_id: userId }),
  }).then(handleResponse);

export const closeEmployeeWeek = (year, week, userId) =>
  fetch(`${API_URL}/clockwise/close-user-week`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ year, week, user_id: userId }),
  }).then(handleResponse);

export const closeDay = (year, week, day, userId = null) =>
  fetch(`${CLOCKWISE_BASE}/close-day`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ year, week, day, ...(userId ? { user_id: userId } : {}) }),
  }).then(handleResponse);

export const closeDayAllUsers = (year, week, day) =>
  fetch(`${CLOCKWISE_BASE}/close-day-all-users`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ year, week, day }),
  }).then(handleResponse);

export const reopenDay = (year, week, day, userId) =>
  fetch(`${API_URL}/clockwise/reopen-day`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ year, week, day, user_id: userId }),
  }).then(handleResponse);

export const closeMultipleDays = (year, week, days, userId = null) =>
  Promise.all((days || []).map((day) => closeDay(year, week, day, userId))).then((results) => {
    const last = results.length > 0 ? results[results.length - 1] : {};
    return {
      ...last,
      message: 'Days closed successfully',
      closed_days_count: results.length,
      results,
    };
  });

export const getEmployeeWeekTasks = (employeeId, year, weekNumber) => {
  const params = new URLSearchParams({ year, weekNumber });
  return fetch(`${CLOCKWISE_BASE}/employee/${employeeId}?${params}`, { headers: authHeaders() }).then(handleResponse);
};

export const getMyWeekTasks = (year, weekNumber) => {
  const params = new URLSearchParams({ year, weekNumber });
  return fetch(`${CLOCKWISE_BASE}/my?${params}`, { headers: authHeaders() }).then(handleResponse);
};

// ─── Overview (Admin) ─────────────────────────────────────────────────────────
export const searchTimesheets = (filters) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => { if (v !== null && v !== undefined && v !== '') params.append(k, v); });
  return fetch(`${API_URL}/clockwise/search?${params}`, { headers: authHeaders() }).then(handleResponse);
};

export const getAllTasks = () => searchTimesheets({});

// ─── Favorites ────────────────────────────────────────────────────────────────
export const getFavorites = () =>
  fetch(`${CLOCKWISE_BASE}/favorites`, { headers: authHeaders() }).then(handleResponse);

export const addFavorite = (type, referenceId) =>
  fetch(`${CLOCKWISE_BASE}/favorites`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ type, reference_id: referenceId }),
  }).then(handleResponse);

export const removeFavorite = (type, referenceId) =>
  fetch(`${CLOCKWISE_BASE}/favorites`, {
    method: 'DELETE',
    headers: authHeaders(),
    body: JSON.stringify({ type, reference_id: referenceId }),
  }).then(handleResponse);
